import { Alert, CircularProgress, Typography } from "@mui/material";
import * as React from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import styles from "./OAuthCallbackPage.module.css";
import type {
  OAuthCallbackPendingView,
  OAuthCallbackView,
} from "./resolveOAuthCallbackView";

/** postMessage structurally clones its payload, so a JSX failure reason cannot travel. */
const OPENER_FALLBACK_MESSAGE = "Roboto could not complete the authorization.";

/**
 * The `type` values a callback posts to its opener. Each provider defines one shared const
 * (e.g. `SLACK_OAUTH_MESSAGES`) that BOTH the sender and the listening page import, so a typo
 * on either side is a compile error rather than a message nobody receives.
 */
export interface OAuthMessageTypes {
  success: string;
  error: string;
}

/** The copy and wiring every caller supplies, whether or not its view can be "loading". */
export interface OAuthCallbackPageBaseProps {
  messageTypes: OAuthMessageTypes;
  successMessage: React.ReactNode;
  /** Wraps the failure reason in call-site copy — including where to retry. */
  renderError: (reason: React.ReactNode) => React.ReactNode;
  /** Where a standalone (non-popup) tab lands once the exchange succeeds. */
  standaloneRedirectPath: string;
}

/**
 * A route loader settles the outcome before the page ever renders, so those callers have no
 * loading state to caption; a page that exchanges in its own query does, and owes the caption.
 * Two members rather than one optional prop, so a spinner can never render without its label.
 */
export type OAuthCallbackPageProps = OAuthCallbackPageBaseProps &
  (
    | { view: OAuthCallbackView; loadingMessage?: never }
    | { view: OAuthCallbackPendingView; loadingMessage: React.ReactNode }
  );

/**
 * Where an OAuth redirect lands, shared by every provider's callback route. Usually renders
 * inside the popup the integration page opened and reports the outcome to the opener;
 * standalone, it redirects to the integration page.
 */
export function OAuthCallbackPage({
  view,
  messageTypes,
  loadingMessage,
  successMessage,
  renderError,
  standaloneRedirectPath,
}: OAuthCallbackPageProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  // Reports the settled view to the opener exactly once per document. The latch is what makes
  // that true: stripping the query below re-runs this effect, and a second success message
  // would be indistinguishable from a second install to an opener that refreshes on it.
  // Contains no exchange call either, so a remount can never re-spend the code.
  const reported = React.useRef(false);
  const queryString = searchParams.toString();
  React.useEffect(() => {
    if (view.status === "loading" || reported.current) {
      return;
    }
    reported.current = true;
    const opener = window.opener as Window | null;
    if (view.status === "error") {
      opener?.postMessage(
        {
          type: messageTypes.error,
          message:
            typeof view.message === "string"
              ? view.message
              : OPENER_FALLBACK_MESSAGE,
        },
        window.location.origin,
      );
      if (view.stripQuery && queryString !== "") {
        setSearchParams({}, { replace: true });
      }
      return;
    }
    if (opener) {
      opener.postMessage(
        { type: messageTypes.success },
        window.location.origin,
      );
      // Strip before close: a browser that refuses window.close() keeps showing this URL,
      // and reloading it would re-exchange in a fresh document.
      if (queryString !== "") {
        setSearchParams({}, { replace: true });
      }
      window.close();
      return;
    }
    navigate(standaloneRedirectPath, { replace: true });
  }, [
    view,
    messageTypes,
    navigate,
    setSearchParams,
    standaloneRedirectPath,
    queryString,
  ]);

  return (
    <div className={styles.page}>
      {view.status === "loading" && (
        <div className={styles.loadingRow}>
          <CircularProgress size={20} />
          <Typography>{loadingMessage}</Typography>
        </div>
      )}
      {view.status === "success" && (
        <Alert severity="success">{successMessage}</Alert>
      )}
      {view.status === "error" && (
        <Alert severity="error">{renderError(view.message)}</Alert>
      )}
    </div>
  );
}
